import { Controller } from "@hotwired/stimulus";
import type { FilterMethod } from "~/components/analytics/shared/type";
import { parseTagParams, valueForFilterMethod } from "~/components/analytics/shared/tagHelpers";

// Connects with data-controller="test-engine-filters"
export default class extends Controller<HTMLElement> {
  static targets = ["tagDialog", "tagDialogKey", "tagDialogValue", "tagDialogFilterMethod"];

  declare tagDialogTarget: HTMLDialogElement;
  declare tagDialogKeyTarget: HTMLInputElement;
  declare tagDialogValueTarget: HTMLInputElement;
  declare tagDialogFilterMethodTarget: HTMLSelectElement;

  // If turboFrameId is set, it will use Turbo.visit to visit the new URL within the turbo frame.
  // Otherwise, it will visit the new URL at the page level.
  declare turboFrameId?: string;
  declare turboFrame: Element | null;
  declare url: URL;

  connect() {
    this.turboFrameId = this.element.dataset.turboFrameId;
    this.turboFrame = this.element.closest(`turbo-frame#${this.turboFrameId}`);
    if (this.turboFrame) {
      this.url = new URL(this.turboFrame.getAttribute("src") as string);
    } else {
      this.url = new URL(window.location.href);
    }
  }

  // Set filter in the URL params and visit the new URL.
  // This action is triggered when the filter option is clicked, except for tags.
  setFilter(event: Event) {
    event.preventDefault();

    const target = event.currentTarget as HTMLElement;
    const name = target.getAttribute("data-name") as string;
    const value = target.getAttribute("data-value") as string;
    const filterMethod = target.getAttribute("data-filter-method") as FilterMethod;

    const params = new URLSearchParams(this.url.search);

    switch (name) {
      case "labels":
      case "owners":
      case "suites":
        this.setArrayParam(params, name, value);
        break;
      case "result":
        this.setTagParam(params, "result", value, filterMethod);
        break;
      case "group_by":
        if (value === "none") {
          this.convertGroupResultFilterToEquals(params);
        }
        params.set(name, value);
        break;
      default:
        if (name.startsWith("tag.")) {
          const tagKey = name.replace("tag.", "");
          this.setTagParam(params, tagKey, value, filterMethod);
        } else if (value === "") {
          params.delete(name);
        } else {
          params.set(name, value);
        }
        break;
    }

    this.resetPagination(params);

    this.url.search = params.toString();
    Turbo.visit(this.url.toString(), { frame: this.turboFrameId });
  }

  // Set tag filter in the URL params and visit the new URL.
  // This action is triggered when the tag dialog form is submitted.
  setTagFilter(event: Event) {
    event.preventDefault();

    const form = event.currentTarget as HTMLFormElement;
    const filterMethod = form.filter_method.value;
    const tagValue = form.tag_value.value;
    const tagKey = form.tag_key.value;

    const params = new URLSearchParams(this.url.search);

    // We use tag dialog for setting custom branch, if the form is submitted for `scm.branch` tag key,
    // we set the branch param instead of tags param.
    if (tagKey === "scm.branch") {
      params.set("branch", valueForFilterMethod(filterMethod, tagValue));
    } else {
      this.setTagParam(params, tagKey, tagValue, filterMethod);
    }

    this.resetPagination(params);

    this.url.search = params.toString();
    Turbo.visit(this.url.toString(), { frame: this.turboFrameId });
  }

  // Open the tag dialog for a specific tag and set the key and value for the form in the dialog.
  // This action is trigger when the tag option is clicked.
  openTagDialog(event: Event) {
    const target = event.currentTarget as HTMLElement;
    const key = target.getAttribute("data-tag-key") as string;
    const value = target.getAttribute("data-value") as string;
    const filterMethod = target.getAttribute("data-filter-method") as string;

    // set filter method options based on tag key
    this.populateFilterMethodOptions(key, filterMethod);

    // Set the tag key and value for the form in the dialog
    this.tagDialogKeyTarget.value = key;
    this.tagDialogFilterMethodTarget.value = filterMethod;
    this.tagDialogValueTarget.value = value;

    this.tagDialogTarget.showModal();
  }

  // Hide options that do not match the search input.
  // If there are no matching options, show the empty state.
  searchFilterOptions(event: Event) {
    const target = event.currentTarget as HTMLInputElement;
    const search = target.value.toLowerCase();
    const groupName = target.name;

    const options = document.querySelectorAll(
      `[data-filter-label][data-search-group="${groupName}"]`,
    );

    let filteredOptionsCount = 0;

    options?.forEach((option) => {
      if (option.getAttribute("title")?.toLowerCase().includes(search)) {
        option.closest("li")?.classList.remove("hidden");
        filteredOptionsCount++;
      } else {
        option.closest("li")?.classList.add("hidden");
      }
    });

    // Show/hide empty state
    const emptyState = document.querySelector(`[data-empty-group="${groupName}"]`);

    if (filteredOptionsCount === 0) {
      emptyState?.classList.remove("hidden");
    } else {
      emptyState?.classList.add("hidden");
    }
  }

  // Dynamically position the submenu relative to its trigger to compensate
  // for vertical scroll/offset within the parent menu container.
  positionSubmenu(event: Event) {
    const trigger = event.currentTarget as HTMLElement;
    const li = trigger.closest("li");
    const submenu = li?.querySelector<HTMLElement>("[data-submenu]");
    if (!li || !submenu) {
      return;
    }

    const containingBlock = submenu.offsetParent as HTMLElement | null;
    if (containingBlock) {
      const liRect = li.getBoundingClientRect();
      const cbRect = containingBlock.getBoundingClientRect();
      submenu.style.top = `${liRect.top - cbRect.top}px`;
    } else {
      submenu.style.top = "0px";
    }
  }

  closeTagDialog() {
    this.tagDialogTarget.close();

    // Reset the tag key and value for the dialog
    this.tagDialogKeyTarget.value = "";
    this.tagDialogFilterMethodTarget.value = "";
    this.tagDialogValueTarget.value = "";
  }

  private populateFilterMethodOptions(key: string, selectedFilterMethod: string) {
    // Define filter options based on tag key
    const filterOptions =
      key === "scm.branch"
        ? [
            { label: "is", value: "equals" },
            { label: "starts with", value: "starts_with" },
          ]
        : [
            { label: "is", value: "equals" },
            { label: "is not", value: "not_equals" },
            { label: "starts with", value: "starts_with" },
          ];

    const select = this.tagDialogFilterMethodTarget;
    const existingOptions = Array.from(select.options);

    // Check if we need to update the options
    const needsUpdate =
      existingOptions.length !== filterOptions.length ||
      existingOptions.some(
        (option, index) => !filterOptions[index] || option.value !== filterOptions[index].value,
      );

    if (needsUpdate) {
      // Only clear and rebuild if the structure has changed
      select.innerHTML = "";
      filterOptions.forEach(({ label, value }, index) => {
        const isDefault = index === 0;
        select.appendChild(new Option(label, value, isDefault, false));
      });
    }

    // Always update the selected value
    select.value = selectedFilterMethod;
  }

  // When switching group_by to "none", convert any group-specific result filter
  // (group_contains or group_only) to a plain "equals" filter, preserving the value.
  private convertGroupResultFilterToEquals(params: URLSearchParams) {
    const parsedTags = parseTagParams(params.get("tags"));
    const resultTag = parsedTags.tags.find((tag) => tag.startsWith("result:"));

    if (resultTag) {
      const rawValue = resultTag.split(":").slice(1).join(":");
      if (rawValue.startsWith("^") || rawValue.startsWith("~")) {
        this.setTagParam(params, "result", rawValue.slice(1), "equals");
      }
    }
  }

  private setArrayParam(params: URLSearchParams, name: string, value: string) {
    const filterParam = params.get(name) || "";
    const filters = new Set<string>();

    filterParam
      .split(",")
      .filter(Boolean)
      .forEach((filter) => {
        filters.add(filter);
      });

    // Add value to the array if it doesn't exist, or remove it if it does
    if (filters.has(value)) {
      filters.delete(value);
    } else {
      filters.add(value);
    }

    if (filters.size === 0) {
      params.delete(name);
    } else {
      params.set(name, [...filters].join(","));
    }
  }

  private setTagParam(
    params: URLSearchParams,
    key: string,
    value: string,
    filterMethod: FilterMethod,
  ) {
    const tags = parseTagParams(params.get("tags"));
    const tagValue = valueForFilterMethod(filterMethod, value);
    const updatedTags = tagValue ? tags.includeByKey(key, tagValue) : tags.excludeByKey(key);

    updatedTags.tags.length
      ? params.set("tags", updatedTags.toQueryString())
      : params.delete("tags");
  }

  // When updating the filters, we reset the page to the first one to avoid the
  // user landing on a page that has no results.
  private resetPagination(params) {
    params.set("page", 1);
  }
}
